import {
  Tag,
  Shield,
  Plus,
  Trash2,
  MapPin,
  ChevronDown,
  ChevronRight,
  Edit2,
  Search,
  Store,
  Sun,
  Moon,
  Layers,
} from "lucide-react";
import { useState, useEffect } from "react";
import {
  getAdminCategoriesV2,
  addSubcategory,
  deleteSubcategory,
  updateSubcategory,
  addRootCategory,
  deleteRootCategory,
  patchRootCategoryFields,
  checkCategoryInStore,
  WALMART_DEFAULT_CATEGORY_MAX_PAGES,
  type CategoryV2,
  type Subcategory,
  addHomeDepotSubcategory,
  deleteHomeDepotSubcategory,
  getAdminUltimaHomeDepotCategories,
  patchUltimaHomeDepotCategorySchedule,
  updateHomeDepotSubcategory,
  getAdminUltimaAmazonCategories,
  postUltimaAmazonCategory,
  patchUltimaAmazonCategory,
  deleteUltimaAmazonCategory,
  checkUltimaAmazonCategory,
  type HomeDepotCategory,
  type UltimaHomeDepotCategory,
  type UltimaAmazonCategory,
  type UltimaAmazonCategoryCheckResult,
} from "../../services/CategoriesService";
import { useStores } from "../../hooks/useStores";
import type { WalmartStore } from "../../utils/storeUtils";

type Retailer = "walmart" | "home_depot" | "amazon";

export default function CategoriesTab() {
  const [retailer, setRetailer] = useState<Retailer>("walmart");
  const [categories, setCategories] = useState<CategoryV2[]>([]);
  const [homeDepotCategories, setHomeDepotCategories] = useState<
    UltimaHomeDepotCategory[]
  >([]);
  const [amazonCategories, setAmazonCategories] = useState<UltimaAmazonCategory[]>([]);
  const [showAddAmazon, setShowAddAmazon] = useState(false);
  const [newAmazon, setNewAmazon] = useState({ id: "", label: "", url: "" });
  const [amazonProbe, setAmazonProbe] = useState<UltimaAmazonCategoryCheckResult | null>(
    null
  );
  const [isCheckingAmazon, setIsCheckingAmazon] = useState(false);
  const [editingAmazonId, setEditingAmazonId] = useState<string | null>(null);
  const [editAmazonDraft, setEditAmazonDraft] = useState({ id: "", label: "", url: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedRootKeys, setExpandedRootKeys] = useState<Set<string>>(
    new Set()
  );
  const [searchTerm, setSearchTerm] = useState("");
  
  // Load stores from S3
  const { stores: walmartStores } = useStores<WalmartStore[]>("walmart");

  // Add root category form (Walmart only)
  const [showAddRootCategory, setShowAddRootCategory] = useState(false);
  const [newRootKey, setNewRootKey] = useState("");

  // Add subcategory form
  const [showAddSubcategory, setShowAddSubcategory] = useState<string | null>(
    null
  );
  const [newSubcategory, setNewSubcategory] = useState({
    name: "",
    id: "",
  });
  // For Home Depot: URL input that will be parsed
  const [categoryUrl, setCategoryUrl] = useState("");

  // Walmart-specific: store selection
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [storeSearchTerm, setStoreSearchTerm] = useState("");
  const [showStoreDropdown, setShowStoreDropdown] = useState(false);
  const [isCheckingCategory, setIsCheckingCategory] = useState(false);
  const [categoryCheckResult, setCategoryCheckResult] = useState<{
    exists: boolean;
    totalItems?: number;
    categoryTitle?: string;
    storeName?: string;
  } | null>(null);

  // Edit subcategory
  const [editingSubcategory, setEditingSubcategory] = useState<{
    rootKey: string;
    subcategoryId: string;
  } | null>(null);
  const [editSubcategoryData, setEditSubcategoryData] = useState({
    name: "",
  });
  const [maxPagesDraft, setMaxPagesDraft] = useState<Record<string, string>>({});
  const [scheduleDraft, setScheduleDraft] = useState<
    Record<string, { morning?: boolean; afternoon?: boolean }>
  >({});
  const [savingWalmartRootKey, setSavingWalmartRootKey] = useState<string | null>(null);
  const [savingHomeDepotCategoryId, setSavingHomeDepotCategoryId] = useState<string | null>(null);

  // Load categories when component mounts or retailer changes
  useEffect(() => {
    loadCategories();
  }, [retailer]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".store-dropdown-container")) {
        setShowStoreDropdown(false);
      }
    };

    if (showStoreDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showStoreDropdown]);

  useEffect(() => {
    setAmazonProbe(null);
  }, [newAmazon.url]);

  // Filter stores based on search term
  const filteredStores = (walmartStores || [])
    .filter((store) => {
      const searchLower = storeSearchTerm.toLowerCase();
      return (
        store.store_id.includes(searchLower) ||
        store.address.toLowerCase().includes(searchLower) ||
        store.postal_code.includes(searchLower)
      );
    })
    .slice(0, 50); // Limit to 50 results for performance

  // Get store name by ID
  const getStoreNameById = (storeId: string) => {
    const store = (walmartStores || []).find((s) => s.store_id === storeId);
    return store ? `Store ${storeId} - ${store.address}` : `Store ${storeId}`;
  };

  // Extract Home Depot category ID from URL
  const extractHomeDepotCategoryId = (url: string): string => {
    if (!url) return "";

    const trimmed = url.trim();

    // Check if it's already just an ID (doesn't contain http or N-)
    if (
      !trimmed.includes("http") &&
      !trimmed.includes("N-") &&
      trimmed.length > 0
    ) {
      return trimmed;
    }

    // Try to extract from URL pattern: .../N-<categoryId> or .../N-<categoryId>?...
    const match = trimmed.match(/N-([a-zA-Z0-9]+)/);
    if (match && match[1]) {
      return match[1];
    }

    // If no match, try to extract anything after the last /N- and before ? or end
    const parts = trimmed.split("/");
    const lastPart = parts[parts.length - 1];
    if (lastPart.startsWith("N-")) {
      // Remove query parameters if any
      const idPart = lastPart.split("?")[0];
      return idPart.substring(2);
    }

    return "";
  };

  // Handle Home Depot URL/category ID input
  const handleHomeDepotCategoryInput = (value: string) => {
    setCategoryUrl(value);
    const extractedId = extractHomeDepotCategoryId(value);
    if (extractedId) {
      setNewSubcategory((prev) => ({
        ...prev,
        id: extractedId,
      }));
    }
  };

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      if (retailer === "walmart") {
        const categoryData = await getAdminCategoriesV2();
        setCategories(categoryData);
      } else if (retailer === "home_depot") {
        const categoryData = await getAdminUltimaHomeDepotCategories();
        setHomeDepotCategories(categoryData);
      } else {
        const categoryData = await getAdminUltimaAmazonCategories();
        setAmazonCategories(categoryData);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load categories";
      setError(errorMessage);
      console.error("Error loading categories:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleRootCategory = (rootKey: string) => {
    const newExpanded = new Set(expandedRootKeys);
    if (newExpanded.has(rootKey)) {
      newExpanded.delete(rootKey);
    } else {
      newExpanded.add(rootKey);
    }
    setExpandedRootKeys(newExpanded);
  };

  const handleCheckCategory = async () => {
    if (!newSubcategory.id.trim() || !selectedStoreId) {
      setError("Category ID and store are required");
      return;
    }

    try {
      setIsCheckingCategory(true);
      setError(null);
      setCategoryCheckResult(null);

      const result = await checkCategoryInStore(
        newSubcategory.id,
        selectedStoreId
      );
      const storeName = getStoreNameById(selectedStoreId);

      setCategoryCheckResult({
        exists: result.exists,
        totalItems: result.totalItems,
        categoryTitle: result.categoryTitle,
        storeName,
      });

      // Auto-fill the name if we got a category title and name is empty
      if (
        result.exists &&
        result.categoryTitle &&
        !newSubcategory.name.trim()
      ) {
        setNewSubcategory((prev) => ({
          ...prev,
          name: result.categoryTitle || "",
        }));
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to check category in store";
      setError(errorMessage);
      console.error("Error checking category:", err);
    } finally {
      setIsCheckingCategory(false);
    }
  };

  const handleAddRootCategory = async () => {
    if (!newRootKey.trim()) {
      setError("Root key is required");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await addRootCategory(newRootKey);
      setNewRootKey("");
      setShowAddRootCategory(false);
      await loadCategories();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to add root category";
      setError(errorMessage);
      console.error("Error adding root category:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRootCategory = async (rootKey: string) => {
    if (
      !confirm(
        `Are you sure you want to delete the root category "${rootKey}" and all its subcategories?`
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      if (retailer === "walmart") {
        await deleteRootCategory(rootKey);
      }
      // Note: Home Depot doesn't support deleting root categories
      await loadCategories();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to delete root category";
      setError(errorMessage);
      console.error("Error deleting root category:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubcategory = async (rootKey: string) => {
    if (!newSubcategory.id.trim()) {
      setError("Category ID is required");
      return;
    }

    if (retailer === "walmart") {
      // Walmart validation
      if (!selectedStoreId) {
        setError("Please select a store");
        return;
      }

      if (!categoryCheckResult?.exists) {
        setError(
          "Please check category availability in the selected store first"
        );
        return;
      }

      if (!categoryCheckResult?.categoryTitle) {
        setError(
          "Category name was not retrieved from Walmart. Please check the category again."
        );
        return;
      }

      try {
        setLoading(true);
        setError(null);
        // Use the category title from the check result
        const subcategoryToAdd = {
          ...newSubcategory,
          name: categoryCheckResult.categoryTitle || "",
        };
        await addSubcategory(rootKey, subcategoryToAdd);
        setNewSubcategory({ name: "", id: "" });
        setSelectedStoreId("");
        setStoreSearchTerm("");
        setCategoryCheckResult(null);
        setShowAddSubcategory(null);
        await loadCategories();
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to add subcategory";
        setError(errorMessage);
        console.error("Error adding subcategory:", err);
      } finally {
        setLoading(false);
      }
    } else {
      // Home Depot - streamlined, no validation
      if (!newSubcategory.name.trim()) {
        setError("Category name is required");
        return;
      }

      try {
        setLoading(true);
        setError(null);
        await addHomeDepotSubcategory(rootKey, {
          id: newSubcategory.id,
          name: newSubcategory.name,
        });
        setNewSubcategory({ name: "", id: "" });
        setCategoryUrl("");
        setShowAddSubcategory(null);
        await loadCategories();
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to add subcategory";
        setError(errorMessage);
        console.error("Error adding subcategory:", err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeleteSubcategory = async (
    rootKey: string,
    subcategoryId: string
  ) => {
    if (!confirm("Are you sure you want to delete this subcategory?")) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      if (retailer === "walmart") {
        await deleteSubcategory(rootKey, subcategoryId);
      } else {
        await deleteHomeDepotSubcategory(rootKey, subcategoryId);
      }
      await loadCategories();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to delete subcategory";
      setError(errorMessage);
      console.error("Error deleting subcategory:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubcategory = (rootKey: string, subcategory: Subcategory) => {
    setEditingSubcategory({ rootKey, subcategoryId: subcategory.id });
    setEditSubcategoryData({
      name: subcategory.name,
    });
  };

  const handleUpdateSubcategory = async () => {
    if (!editingSubcategory || !editSubcategoryData.name.trim()) {
      setError("Subcategory name is required");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      if (retailer === "walmart") {
        await updateSubcategory(
          editingSubcategory.rootKey,
          editingSubcategory.subcategoryId,
          editSubcategoryData
        );
      } else {
        await updateHomeDepotSubcategory(
          editingSubcategory.rootKey,
          editingSubcategory.subcategoryId,
          editSubcategoryData
        );
      }
      setEditingSubcategory(null);
      setEditSubcategoryData({ name: "" });
      await loadCategories();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to update subcategory";
      setError(errorMessage);
      console.error("Error updating subcategory:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingSubcategory(null);
    setEditSubcategoryData({ name: "" });
    setError(null);
  };

  const handleSaveRootWalmartSettings = async (rootKey: string) => {
    const cat = categories.find((c) => c.rootKey === rootKey);
    if (!cat) return;
    const fromDraft = maxPagesDraft[rootKey];
    const raw = (fromDraft !== undefined ? fromDraft : cat.maxPages != null ? String(cat.maxPages) : "").trim();
    const sd = scheduleDraft[rootKey];
    const runMorning = sd?.morning ?? (cat.runMorning ?? true);
    const runAfternoon = sd?.afternoon ?? (cat.runAfternoon ?? true);
    try {
      setSavingWalmartRootKey(rootKey);
      setError(null);
      let maxPages: number | null;
      if (raw === "") maxPages = null;
      else {
        const n = parseInt(raw, 10);
        if (Number.isNaN(n) || n < 1 || n > 200) {
          setError("Max pages must be between 1 and 200, or leave empty for default");
          return;
        }
        maxPages = n;
      }
      await patchRootCategoryFields(rootKey, { maxPages, runMorning, runAfternoon });
      setCategories((prev) =>
        prev.map((c) =>
          c.rootKey === rootKey
            ? { ...c, maxPages, runMorning, runAfternoon }
            : c
        )
      );
      setMaxPagesDraft((d) => {
        const next = { ...d };
        delete next[rootKey];
        return next;
      });
      setScheduleDraft((d) => {
        const next = { ...d };
        delete next[rootKey];
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save crawl settings");
      console.error(err);
    } finally {
      setSavingWalmartRootKey(null);
    }
  };

  const handleSaveHomeDepotSchedule = async (categoryId: string) => {
    const cat = homeDepotCategories.find((c) => c.n === categoryId);
    if (!cat) return;
    const sd = scheduleDraft[categoryId];
    const runMorning = sd?.morning ?? (cat.runMorning ?? true);
    const runAfternoon = sd?.afternoon ?? (cat.runAfternoon ?? true);
    try {
      setSavingHomeDepotCategoryId(categoryId);
      setError(null);
      await patchUltimaHomeDepotCategorySchedule(categoryId, { runMorning, runAfternoon });
      setHomeDepotCategories((prev) =>
        prev.map((c) =>
          c.n === categoryId ? { ...c, runMorning, runAfternoon } : c
        )
      );
      setScheduleDraft((d) => {
        const next = { ...d };
        delete next[categoryId];
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save Home Depot schedule");
      console.error(err);
    } finally {
      setSavingHomeDepotCategoryId(null);
    }
  };

  const handleCheckAmazonCategory = async () => {
    if (!newAmazon.url.trim()) {
      setError("URL is required to check");
      return;
    }
    setIsCheckingAmazon(true);
    setError(null);
    setAmazonProbe(null);
    try {
      const r = await checkUltimaAmazonCategory(newAmazon.url.trim());
      setAmazonProbe(r);
      if (!r.valid) {
        if (r.message) setError(r.message);
      } else {
        setError(null);
      }
    } catch (err) {
      setAmazonProbe(null);
      setError(err instanceof Error ? err.message : "Listing check failed");
    } finally {
      setIsCheckingAmazon(false);
    }
  };

  const handleAddAmazon = async () => {
    if (!newAmazon.id.trim() || !newAmazon.label.trim()) {
      setError("id and label are required");
      return;
    }
    if (!amazonProbe?.valid) {
      setError("Check the listing first. Wait until Unwrangle confirms products were found.");
      return;
    }
    if (
      !confirm(
        `Add category "${newAmazon.label.trim()}" (${newAmazon.id.trim()}) with ${amazonProbe.totalResults.toLocaleString()} products (~${amazonProbe.estimatedPages} pages)?`
      )
    ) {
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await postUltimaAmazonCategory({
        id: newAmazon.id.trim(),
        label: newAmazon.label.trim(),
        url: amazonProbe.filteredUrl,
      });
      setNewAmazon({ id: "", label: "", url: "" });
      setAmazonProbe(null);
      setShowAddAmazon(false);
      await loadCategories();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add Amazon category");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAmazon = async (id: string) => {
    if (!confirm(`Delete Amazon category "${id}"?`)) return;
    try {
      setLoading(true);
      setError(null);
      await deleteUltimaAmazonCategory(id);
      await loadCategories();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete");
    } finally {
      setLoading(false);
    }
  };

  const startEditAmazon = (c: UltimaAmazonCategory) => {
    setEditingAmazonId(c.id);
    setEditAmazonDraft({ id: c.id, label: c.label, url: c.url });
  };

  const handleSaveAmazonEdit = async () => {
    if (!editingAmazonId || !editAmazonDraft.label.trim() || !editAmazonDraft.url.trim()) {
      setError("label and url are required");
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await patchUltimaAmazonCategory(editingAmazonId, {
        id: editAmazonDraft.id.trim(),
        label: editAmazonDraft.label.trim(),
        url: editAmazonDraft.url.trim(),
      });
      setEditingAmazonId(null);
      await loadCategories();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const getTotalSubcategories = () => {
    if (retailer === "walmart") {
      return categories.reduce(
        (total, cat) => total + cat.subcategories.length,
        0
      );
    }
    if (retailer === "amazon") {
      return amazonCategories.length;
    }
    return homeDepotCategories.length;
  };

  const getCurrentCategories = (): CategoryV2[] | HomeDepotCategory[] => {
    if (retailer === "walmart") return categories;
    if (retailer === "amazon") return [];
    return homeDepotCategories.map((cat) => ({
      rootKey: cat.title,
      subcategories: [{ name: cat.title, id: cat.n }],
      runMorning: cat.runMorning,
      runAfternoon: cat.runAfternoon,
    }));
  };

  // Filter categories based on search term
  const filteredCategories = getCurrentCategories().filter((category) => {
    if (!searchTerm.trim()) return true;

    const searchLower = searchTerm.toLowerCase();

    // Check if root category matches
    const rootMatches = category.rootKey.toLowerCase().includes(searchLower);

    // Check if any subcategory matches
    const subcategoryMatches = category.subcategories.some(
      (sub) =>
        sub.name.toLowerCase().includes(searchLower) ||
        sub.id.toLowerCase().includes(searchLower)
    );

    return rootMatches || subcategoryMatches;
  });

  const filteredAmazon = amazonCategories
    .filter((c) => {
      if (!searchTerm.trim()) return true;
      const q = searchTerm.toLowerCase();
      return (
        c.id.toLowerCase().includes(q) ||
        c.label.toLowerCase().includes(q) ||
        c.url.toLowerCase().includes(q)
      );
    })
    .sort((a, b) => a.id.localeCompare(b.id));

  const deferCategorySpinner =
    (retailer === "walmart" && showAddRootCategory) ||
    (retailer === "amazon" && showAddAmazon);

  return (
    <>
      {/* Retailer Selector */}
      <div className="mb-6 inline-flex p-1 bg-gray-100 dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-lg">
        {([
          { id: "walmart", label: "Walmart", accent: "blue" as const },
          { id: "home_depot", label: "Home Depot", accent: "orange" as const },
          { id: "amazon", label: "Amazon", accent: "amber" as const },
        ] as const).map((opt) => {
          const active = retailer === opt.id;
          const activeCls =
            opt.accent === "blue"
              ? "bg-white dark:bg-dark-900 text-blue-700 dark:text-blue-300 shadow-sm ring-1 ring-blue-200 dark:ring-blue-900/50"
              : opt.accent === "orange"
                ? "bg-white dark:bg-dark-900 text-orange-700 dark:text-orange-300 shadow-sm ring-1 ring-orange-200 dark:ring-orange-900/50"
                : "bg-white dark:bg-dark-900 text-amber-800 dark:text-amber-300 shadow-sm ring-1 ring-amber-200 dark:ring-amber-900/50";
          return (
            <button
              key={opt.id}
              onPointerDown={() => {
                setRetailer(opt.id);
                setExpandedRootKeys(new Set());
                setShowAddAmazon(false);
                setEditingAmazonId(null);
                setError(null);
              }}
              className={`flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-md transition-all ${active ? activeCls : "text-gray-600 dark:text-dark-300 hover:text-gray-900 dark:hover:text-white"}`}
            >
              <Store className="w-4 h-4" />
              {opt.label}
            </button>
          );
        })}
      </div>

      {/* Categories Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {[
          {
            label: retailer === "walmart" ? "Root Categories" : "Categories",
            value: retailer === "amazon" ? filteredAmazon.length : filteredCategories.length,
            icon: Tag,
            tone: "text-blue-600 dark:text-blue-400",
            ring: "ring-blue-500/10",
          },
          {
            label:
              retailer === "walmart"
                ? "Subcategories"
                : retailer === "amazon"
                  ? "Saved rows"
                  : "Scheduled Items",
            value:
              retailer === "amazon" ? amazonCategories.length : getTotalSubcategories(),
            icon: Layers,
            tone: "text-emerald-600 dark:text-emerald-400",
            ring: "ring-emerald-500/10",
          },
          {
            label: "Admin",
            value: "Active",
            icon: Shield,
            tone: "text-purple-600 dark:text-purple-400",
            ring: "ring-purple-500/10",
          },
        ].map((s) => (
          <div
            key={s.label}
            className={`flex items-center gap-3 px-4 py-3 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-lg ring-1 ${s.ring}`}
          >
            <s.icon className={`h-5 w-5 ${s.tone}`} />
            <div className="leading-tight">
              <p className="text-[11px] font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wide">
                {s.label}
              </p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white tabular-nums">
                {s.value}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Categories Management */}
      <div className="bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-dark-700 bg-gray-50/60 dark:bg-dark-900/30">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                Category Management
              </h2>
              <p className="text-xs text-gray-500 dark:text-dark-400 mt-0.5">
                {retailer === "walmart"
                  ? "Walmart · manage root categories and their subcategories"
                  : retailer === "home_depot"
                    ? "Home Depot · schedule Ultima HD categories from the S3 JSON file"
                    : "Amazon · Ultima Amazon category URLs stored in S3 (amazon-categories.json)"}
              </p>
            </div>
            {retailer === "walmart" && (
              <button
                onPointerDown={() => setShowAddRootCategory(true)}
                disabled={editingSubcategory !== null}
                className="bg-red-600 text-white px-3.5 py-1.5 text-sm rounded-lg hover:bg-red-700 transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                <Plus className="w-4 h-4" />
                Add Root Category
              </button>
            )}
            {retailer === "amazon" && (
              <button
                onPointerDown={() => {
                  setShowAddAmazon(true);
                  setNewAmazon({ id: "", label: "", url: "" });
                  setAmazonProbe(null);
                  setError(null);
                }}
                disabled={editingAmazonId !== null || loading || isCheckingAmazon}
                className="bg-amber-600 text-white px-3.5 py-1.5 text-sm rounded-lg hover:bg-amber-700 transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                <Plus className="w-4 h-4" />
                Add category
              </button>
            )}
          </div>

          {/* Search Input */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400 dark:text-dark-400" />
            </div>
            <input
              type="text"
              placeholder="Search categories and subcategories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-dark-700 rounded-lg bg-white dark:bg-dark-800 placeholder-gray-400 dark:placeholder-dark-500 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-500 transition-colors"
            />
          </div>
        </div>
        <div className="p-4 sm:p-5">
          {error && (
            <div className="mb-4 px-3 py-2 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
            </div>
          )}

          {/* Add Root Category Form (Walmart only) */}
          {showAddRootCategory && retailer === "walmart" && (
            <div className="mb-6 p-4 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Add New Root Category
              </h3>
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-1">
                    Root Key
                  </label>
                  <input
                    type="text"
                    value={newRootKey}
                    onChange={(e) => setNewRootKey(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-dark-600 rounded-md bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="Enter root category key"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onPointerDown={() => {
                      setShowAddRootCategory(false);
                      setNewRootKey("");
                      setError(null);
                    }}
                    className="px-4 py-2 text-gray-600 dark:text-dark-300 hover:text-gray-800 dark:hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onPointerDown={handleAddRootCategory}
                    disabled={loading || !newRootKey.trim()}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    {loading ? "Adding..." : "Add Root Category"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {showAddAmazon && retailer === "amazon" && (
            <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-950/25 border border-amber-200 dark:border-amber-900 rounded-lg">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">New Amazon category</h3>
              <p className="text-xs text-gray-600 dark:text-dark-400 mb-3">
                Paste a browse or search URL, run <strong>Check listing</strong> (calls Unwrangle on page 1 with the same filters as Ultima), then confirm to save the normalized URL.
              </p>
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="sm:col-span-1">
                  <label className="block text-xs font-medium text-gray-600 dark:text-dark-400 mb-1">id</label>
                  <input
                    type="text"
                    value={newAmazon.id}
                    onChange={(e) => setNewAmazon((p) => ({ ...p, id: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-dark-600 rounded-md bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500/40"
                    placeholder="e.g. ps5"
                  />
                </div>
                <div className="sm:col-span-1">
                  <label className="block text-xs font-medium text-gray-600 dark:text-dark-400 mb-1">label</label>
                  <input
                    type="text"
                    value={newAmazon.label}
                    onChange={(e) => setNewAmazon((p) => ({ ...p, label: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-dark-600 rounded-md bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500/40"
                    placeholder="Display name"
                  />
                </div>
                <div className="sm:col-span-3">
                  <label className="block text-xs font-medium text-gray-600 dark:text-dark-400 mb-1">search / browse URL</label>
                  <input
                    type="text"
                    value={newAmazon.url}
                    onChange={(e) => setNewAmazon((p) => ({ ...p, url: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-dark-600 rounded-md bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500/40 font-mono"
                    placeholder="https://www.amazon.com/..."
                  />
                </div>
              </div>

              {amazonProbe?.valid && (
                <div className="mt-4 p-3 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900 text-sm text-green-900 dark:text-green-200">
                  <p className="font-medium">Listing looks valid</p>
                  <ul className="mt-2 space-y-1 text-xs text-green-800 dark:text-green-300 list-disc list-inside">
                    <li>
                      <strong>{amazonProbe.totalResults.toLocaleString()}</strong> products reported by Unwrangle
                    </li>
                    <li>
                      ~<strong>{amazonProbe.estimatedPages}</strong> result pages (estimate; Ultima uses at most 20 pages per run)
                    </li>
                    {amazonProbe.sampleProductName ? (
                      <li>
                        Sample: <span className="font-medium">{amazonProbe.sampleProductName}</span>
                      </li>
                    ) : null}
                  </ul>
                  <p className="mt-2 text-[11px] text-green-800/90 dark:text-green-400/90 break-all font-mono">
                    Saved URL (normalized): {amazonProbe.filteredUrl}
                  </p>
                </div>
              )}

              <div className="mt-3 flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  onPointerDown={() => {
                    setShowAddAmazon(false);
                    setNewAmazon({ id: "", label: "", url: "" });
                    setAmazonProbe(null);
                    setError(null);
                  }}
                  className="px-4 py-2 text-sm text-gray-600 dark:text-dark-300 hover:text-gray-900 dark:hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onPointerDown={handleCheckAmazonCategory}
                  disabled={isCheckingAmazon || loading || !newAmazon.url.trim()}
                  className="px-4 py-2 text-sm rounded-lg border border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-200 bg-white dark:bg-dark-800 hover:bg-amber-50 dark:hover:bg-amber-950/40 disabled:opacity-50"
                >
                  {isCheckingAmazon ? "Checking…" : "Check listing"}
                </button>
                <button
                  type="button"
                  onPointerDown={handleAddAmazon}
                  disabled={
                    loading ||
                    isCheckingAmazon ||
                    !amazonProbe?.valid ||
                    !newAmazon.id.trim() ||
                    !newAmazon.label.trim()
                  }
                  className="bg-amber-600 text-white px-4 py-2 text-sm rounded-lg hover:bg-amber-700 disabled:opacity-50"
                >
                  {loading ? "Saving…" : "Confirm add"}
                </button>
              </div>
            </div>
          )}

          {/* Categories List */}
          {loading && !deferCategorySpinner ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
              <p className="mt-2 text-gray-600 dark:text-dark-300">Loading categories...</p>
            </div>
          ) : retailer === "amazon" ? (
            <div className="space-y-2">
              {filteredAmazon.map((c) => (
                <div
                  key={c.id}
                  className="border border-gray-200 dark:border-dark-700 rounded-lg p-4 bg-white dark:bg-dark-800/60"
                >
                  {editingAmazonId === c.id ? (
                    <div className="grid gap-3">
                      <div className="grid gap-3 sm:grid-cols-3">
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-dark-400 mb-1">id</label>
                          <input
                            type="text"
                            value={editAmazonDraft.id}
                            onChange={(e) =>
                              setEditAmazonDraft((d) => ({ ...d, id: e.target.value }))
                            }
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-dark-600 rounded-md bg-white dark:bg-dark-800"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <label className="block text-xs text-gray-500 dark:text-dark-400 mb-1">label</label>
                          <input
                            type="text"
                            value={editAmazonDraft.label}
                            onChange={(e) =>
                              setEditAmazonDraft((d) => ({ ...d, label: e.target.value }))
                            }
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-dark-600 rounded-md bg-white dark:bg-dark-800"
                          />
                        </div>
                        <div className="sm:col-span-3">
                          <label className="block text-xs text-gray-500 dark:text-dark-400 mb-1">URL</label>
                          <textarea
                            value={editAmazonDraft.url}
                            onChange={(e) =>
                              setEditAmazonDraft((d) => ({ ...d, url: e.target.value }))
                            }
                            rows={2}
                            className="w-full px-3 py-2 text-xs font-mono border border-gray-300 dark:border-dark-600 rounded-md bg-white dark:bg-dark-800"
                          />
                        </div>
                      </div>
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onPointerDown={() => {
                            setEditingAmazonId(null);
                            setError(null);
                          }}
                          className="text-sm text-gray-600 dark:text-dark-300"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onPointerDown={handleSaveAmazonEdit}
                          disabled={loading}
                          className="text-sm bg-amber-600 text-white px-3 py-1.5 rounded-lg disabled:opacity-50"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">{c.label}</p>
                        <p className="text-xs text-gray-500 dark:text-dark-400 font-mono mt-0.5">{c.id}</p>
                        <p className="text-[11px] text-gray-600 dark:text-dark-300 break-all font-mono mt-2">{c.url}</p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button
                          type="button"
                          onPointerDown={() => startEditAmazon(c)}
                          disabled={loading || editingAmazonId !== null}
                          className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded-md disabled:opacity-40"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onPointerDown={() => handleDeleteAmazon(c.id)}
                          disabled={loading || editingAmazonId !== null}
                          className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-md disabled:opacity-40"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {filteredAmazon.length === 0 && !loading && (
                <div className="text-center py-8">
                  <Search className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-dark-600" />
                  <p className="text-gray-500 dark:text-dark-300 text-lg font-medium">
                    {searchTerm.trim() ? "No categories match your search" : "No Amazon categories"}
                  </p>
                  <p className="text-gray-400 dark:text-dark-400 text-sm">
                    {searchTerm.trim()
                      ? "Try adjusting your search."
                      : "Add one above or upload amazon-categories.json to S3."}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredCategories.map((rootCategory) => {
                const categoryKey =
                  retailer === "walmart"
                    ? rootCategory.rootKey
                    : rootCategory.subcategories[0]?.id || rootCategory.rootKey;
                const isExpanded = expandedRootKeys.has(categoryKey);
                const isSaving =
                  retailer === "walmart"
                    ? savingWalmartRootKey === categoryKey
                    : savingHomeDepotCategoryId === categoryKey;
                const actionsDisabled = loading || editingSubcategory !== null;
                const morningEffective =
                  scheduleDraft[categoryKey]?.morning ??
                  (rootCategory.runMorning ?? true);
                const afternoonEffective =
                  scheduleDraft[categoryKey]?.afternoon ??
                  (rootCategory.runAfternoon ?? true);
                const maxPagesEffective =
                  maxPagesDraft[categoryKey] !== undefined
                    ? maxPagesDraft[categoryKey]
                    : rootCategory.maxPages != null
                      ? String(rootCategory.maxPages)
                      : "";
                const hasDraft =
                  maxPagesDraft[categoryKey] !== undefined ||
                  scheduleDraft[categoryKey] !== undefined;
                return (
                <div
                  key={categoryKey}
                  className="bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-lg overflow-hidden transition-shadow hover:shadow-sm"
                >
                  {/* Root Category Header */}
                  <div className="px-4 py-3 flex items-center gap-4 flex-wrap">
                    <button
                      onPointerDown={() =>
                        toggleRootCategory(categoryKey)
                      }
                      className="flex items-center gap-3 text-left min-w-0 flex-shrink-0 group"
                    >
                      <span className="p-1 rounded-md text-gray-400 dark:text-dark-500 group-hover:bg-gray-100 dark:group-hover:bg-dark-700 group-hover:text-gray-700 dark:group-hover:text-dark-200 transition-colors">
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </span>
                      <div className="min-w-0">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                          {rootCategory.rootKey}
                        </h3>
                        <p className="text-[11px] text-gray-500 dark:text-dark-400">
                          {retailer === "walmart"
                            ? `${rootCategory.subcategories.length} ${
                                rootCategory.subcategories.length === 1
                                  ? "subcategory"
                                  : "subcategories"
                              }`
                            : `ID: ${categoryKey}`}
                        </p>
                      </div>
                    </button>

                    <div className="flex items-center gap-2 ml-auto flex-wrap">
                      {(retailer === "walmart" || retailer === "home_depot") && (
                        <div className="flex items-center gap-1 px-1 py-1 border border-gray-200 dark:border-dark-700 rounded-lg bg-gray-50 dark:bg-dark-900/40">
                          <button
                            type="button"
                            title="Run before noon EST"
                            disabled={actionsDisabled || isSaving}
                            onPointerDown={() =>
                              setScheduleDraft((d) => ({
                                ...d,
                                [categoryKey]: {
                                  ...(d[categoryKey] || {}),
                                  morning: !morningEffective,
                                  afternoon:
                                    d[categoryKey]?.afternoon ??
                                    (rootCategory.runAfternoon ?? true),
                                },
                              }))
                            }
                            className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md transition-colors disabled:opacity-50 ${
                              morningEffective
                                ? "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300 ring-1 ring-amber-300/60 dark:ring-amber-500/30"
                                : "text-gray-500 dark:text-dark-400 hover:bg-gray-100 dark:hover:bg-dark-700"
                            }`}
                          >
                            <Sun className="w-3.5 h-3.5" />
                            Morning
                          </button>
                          <button
                            type="button"
                            title="Run at/after noon EST"
                            disabled={actionsDisabled || isSaving}
                            onPointerDown={() =>
                              setScheduleDraft((d) => ({
                                ...d,
                                [categoryKey]: {
                                  ...(d[categoryKey] || {}),
                                  morning:
                                    d[categoryKey]?.morning ??
                                    (rootCategory.runMorning ?? true),
                                  afternoon: !afternoonEffective,
                                },
                              }))
                            }
                            className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md transition-colors disabled:opacity-50 ${
                              afternoonEffective
                                ? "bg-indigo-100 text-indigo-800 dark:bg-indigo-500/15 dark:text-indigo-300 ring-1 ring-indigo-300/60 dark:ring-indigo-500/30"
                                : "text-gray-500 dark:text-dark-400 hover:bg-gray-100 dark:hover:bg-dark-700"
                            }`}
                          >
                            <Moon className="w-3.5 h-3.5" />
                            Afternoon
                          </button>
                          {retailer === "walmart" && (
                            <>
                              <div className="w-px h-5 bg-gray-200 dark:bg-dark-700 mx-0.5" />
                              <div className="flex items-center gap-1 pl-1 pr-1">
                                <Layers className="w-3.5 h-3.5 text-gray-400 dark:text-dark-500" />
                                <input
                                  type="number"
                                  min={1}
                                  max={200}
                                  disabled={actionsDisabled || isSaving}
                                  aria-label="Max pages"
                                  value={maxPagesEffective}
                                  placeholder={String(WALMART_DEFAULT_CATEGORY_MAX_PAGES)}
                                  onChange={(e) =>
                                    setMaxPagesDraft((d) => ({
                                      ...d,
                                      [categoryKey]: e.target.value,
                                    }))
                                  }
                                  className="w-12 px-1 py-0.5 text-xs font-medium text-gray-900 dark:text-white bg-transparent border-0 focus:outline-none focus:ring-0 tabular-nums text-center"
                                />
                                <span className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-dark-500">pg</span>
                              </div>
                            </>
                          )}
                          <button
                            type="button"
                            onPointerDown={() =>
                              retailer === "walmart"
                                ? handleSaveRootWalmartSettings(categoryKey)
                                : handleSaveHomeDepotSchedule(categoryKey)
                            }
                            disabled={
                              actionsDisabled || isSaving || !hasDraft
                            }
                            className={`ml-1 px-2.5 py-1 text-xs font-semibold rounded-md transition-colors ${
                              hasDraft
                                ? "bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                                : "text-gray-400 dark:text-dark-500 cursor-not-allowed"
                            }`}
                          >
                            {isSaving ? "…" : "Save"}
                          </button>
                        </div>
                      )}

                      {retailer === "walmart" && (
                        <button
                          onPointerDown={() =>
                            setShowAddSubcategory(rootCategory.rootKey)
                          }
                          disabled={actionsDisabled}
                          title="Add subcategory"
                          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-500/10 hover:bg-blue-100 dark:hover:bg-blue-500/20 rounded-md transition-colors disabled:opacity-50"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">Subcategory</span>
                        </button>
                      )}
                      {retailer === "walmart" && (
                        <button
                          onPointerDown={() =>
                            handleDeleteRootCategory(rootCategory.rootKey)
                          }
                          disabled={actionsDisabled}
                          title="Delete root category"
                          className="p-1.5 text-gray-400 dark:text-dark-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-md transition-colors disabled:opacity-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Add Subcategory Form */}
                  {showAddSubcategory === rootCategory.rootKey && (
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border-b border-gray-200 dark:border-dark-700">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                        Add Subcategory to {rootCategory.rootKey}
                      </h4>
                      {retailer === "walmart" ? (
                        // Walmart form
                        <div className="grid grid-cols-1 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-1">
                              Category ID
                            </label>
                            <input
                              type="text"
                              value={newSubcategory.id}
                              onChange={(e) =>
                                setNewSubcategory({
                                  ...newSubcategory,
                                  id: e.target.value,
                                })
                              }
                              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-600 rounded-md bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                              placeholder="Enter category ID"
                            />
                          </div>

                          {/* Store Selection */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-1">
                              <MapPin className="inline w-4 h-4 mr-1" />
                              Select Store *
                            </label>
                            <div className="relative store-dropdown-container">
                              <input
                                type="text"
                                value={
                                  selectedStoreId
                                    ? getStoreNameById(selectedStoreId)
                                    : storeSearchTerm
                                }
                                onChange={(e) => {
                                  setStoreSearchTerm(e.target.value);
                                  setSelectedStoreId("");
                                  setCategoryCheckResult(null);
                                  setShowStoreDropdown(true);
                                }}
                                onFocus={() => setShowStoreDropdown(true)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-600 rounded-md bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                                placeholder="Search by store ID, address, or zip code... *"
                              />

                              {/* Store Dropdown */}
                              {showStoreDropdown &&
                                storeSearchTerm &&
                                !selectedStoreId && (
                                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-dark-800 border border-gray-300 dark:border-dark-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                    {filteredStores.length > 0 ? (
                                      filteredStores.map((store) => (
                                        <button
                                          key={store.store_id}
                                          type="button"
                                          onPointerDown={() => {
                                            setSelectedStoreId(store.store_id);
                                            setStoreSearchTerm("");
                                            setShowStoreDropdown(false);
                                            setCategoryCheckResult(null);
                                          }}
                                          className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-dark-700 text-sm"
                                        >
                                          <div className="font-medium text-gray-900 dark:text-white">
                                            Store {store.store_id}
                                          </div>
                                          <div className="text-gray-600 dark:text-dark-400 text-xs">
                                            {store.address}
                                          </div>
                                        </button>
                                      ))
                                    ) : (
                                      <div className="px-3 py-2 text-gray-500 dark:text-dark-400 text-sm">
                                        No stores found
                                      </div>
                                    )}
                                  </div>
                                )}
                            </div>

                            {selectedStoreId && (
                              <div className="mt-2 flex gap-2">
                                <button
                                  type="button"
                                  onPointerDown={handleCheckCategory}
                                  disabled={
                                    isCheckingCategory ||
                                    !newSubcategory.id.trim()
                                  }
                                  className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors disabled:opacity-50"
                                >
                                  {isCheckingCategory
                                    ? "Checking..."
                                    : "Check Category"}
                                </button>
                                <button
                                  type="button"
                                  onPointerDown={() => {
                                    setSelectedStoreId("");
                                    setStoreSearchTerm("");
                                    setCategoryCheckResult(null);
                                  }}
                                  className="text-gray-600 dark:text-dark-400 hover:text-gray-800 dark:hover:text-white text-sm transition-colors"
                                >
                                  Clear Store
                                </button>
                              </div>
                            )}

                            {/* Category Check Result */}
                            {categoryCheckResult && (
                              <div
                                className={`mt-2 p-3 rounded-lg ${
                                  categoryCheckResult.exists
                                    ? "bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800"
                                    : "bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800"
                                }`}
                              >
                                {categoryCheckResult.exists ? (
                                  <div>
                                    <p className="text-green-800 dark:text-green-300 text-sm font-medium">
                                      ✓ Category found in{" "}
                                      {categoryCheckResult.storeName}
                                    </p>
                                    {categoryCheckResult.categoryTitle && (
                                      <p className="text-green-800 dark:text-green-300 text-base font-semibold mt-2">
                                        Category Name:{" "}
                                        {categoryCheckResult.categoryTitle}
                                      </p>
                                    )}
                                    {categoryCheckResult.totalItems && (
                                      <p className="text-green-700 dark:text-green-400 text-sm">
                                        This category has{" "}
                                        {categoryCheckResult.totalItems} items
                                        in stock at this store.
                                      </p>
                                    )}
                                    <p className="text-green-700 dark:text-green-400 text-sm mt-1">
                                      Would you like to add this subcategory?
                                    </p>
                                  </div>
                                ) : (
                                  <div>
                                    <p className="text-yellow-800 dark:text-yellow-300 text-sm font-medium">
                                      ⚠ Category not found or has no items in{" "}
                                      {categoryCheckResult.storeName}
                                    </p>
                                    <p className="text-yellow-700 dark:text-yellow-400 text-sm">
                                      This category may not be valid for this
                                      store or may be out of stock.
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        // Home Depot form - streamlined
                        <div className="grid grid-cols-1 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-1">
                              Category URL or ID
                            </label>
                            <input
                              type="text"
                              value={categoryUrl}
                              onChange={(e) =>
                                handleHomeDepotCategoryInput(e.target.value)
                              }
                              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-600 rounded-md bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                              placeholder="Paste URL (e.g., https://www.homedepot.com/b/.../N-5yc1vZc3nrZy0) or enter category ID directly"
                            />
                            <p className="text-xs text-gray-500 dark:text-dark-400 mt-1">
                              Example:{" "}
                              <code className="bg-gray-100 dark:bg-dark-600 px-1 rounded">
                                https://www.homedepot.com/b/Appliances-Freezers-Chest-Freezers/Magic-Chef/N-5yc1vZc3nrZy0
                              </code>
                            </p>
                            {newSubcategory.id && (
                              <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                                Extracted ID:{" "}
                                <strong>{newSubcategory.id}</strong>
                              </p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-1">
                              Category Name *
                            </label>
                            <input
                              type="text"
                              value={newSubcategory.name}
                              onChange={(e) =>
                                setNewSubcategory({
                                  ...newSubcategory,
                                  name: e.target.value,
                                })
                              }
                              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-600 rounded-md bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                              placeholder="Enter a name for this category"
                            />
                          </div>
                        </div>
                      )}
                      <div className="flex justify-end gap-2 mt-4">
                        <button
                          onPointerDown={() => {
                            setShowAddSubcategory(null);
                            setNewSubcategory({
                              name: "",
                              id: "",
                            });
                            setCategoryUrl("");
                            setSelectedStoreId("");
                            setStoreSearchTerm("");
                            setCategoryCheckResult(null);
                            setError(null);
                          }}
                          className="px-4 py-2 text-gray-600 dark:text-dark-300 hover:text-gray-800 dark:hover:text-white transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onPointerDown={() =>
                            handleAddSubcategory(rootCategory.rootKey)
                          }
                          disabled={
                            retailer === "walmart"
                              ? loading ||
                                !categoryCheckResult?.exists ||
                                !selectedStoreId ||
                                !categoryCheckResult?.categoryTitle
                              : loading ||
                                !newSubcategory.id.trim() ||
                                !newSubcategory.name.trim()
                          }
                          className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                          title={
                            retailer === "walmart"
                              ? !selectedStoreId
                                ? "Please select a store first"
                                : !categoryCheckResult?.exists
                                ? "Please check category availability first"
                                : !categoryCheckResult?.categoryTitle
                                ? "Category name not retrieved from Walmart"
                                : ""
                              : !newSubcategory.id.trim()
                              ? "Please enter a category URL or ID"
                              : !newSubcategory.name.trim()
                              ? "Please enter a category name"
                              : ""
                          }
                        >
                          {loading ? "Adding..." : "Add Subcategory"}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Subcategories List */}
                  {expandedRootKeys.has(categoryKey) && (
                    <div className="divide-y divide-gray-200 dark:divide-dark-700">
                      {rootCategory.subcategories.length === 0 ? (
                        <div className="px-4 py-6 text-center text-gray-500 dark:text-dark-400">
                          No subcategories found. Click "Add Subcategory" to
                          create one.
                        </div>
                      ) : (
                        rootCategory.subcategories.map((subcategory) => (
                          <div
                            key={subcategory.id}
                            className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-dark-700"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                {editingSubcategory?.rootKey ===
                                  rootCategory.rootKey &&
                                editingSubcategory?.subcategoryId ===
                                  subcategory.id ? (
                                  <input
                                    type="text"
                                    value={editSubcategoryData.name}
                                    onChange={(e) =>
                                      setEditSubcategoryData({
                                        ...editSubcategoryData,
                                        name: e.target.value,
                                      })
                                    }
                                    className="w-full px-3 py-1 border border-gray-300 dark:border-dark-600 rounded-md bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                                    placeholder="Subcategory name"
                                  />
                                ) : (
                                  <div>
                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                      {subcategory.name}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-dark-400">
                                      ID: {subcategory.id}
                                    </div>
                                  </div>
                                )}
                              </div>
                              {retailer === "walmart" && (
                              <div className="flex items-center gap-2">
                                {editingSubcategory?.rootKey ===
                                  rootCategory.rootKey &&
                                editingSubcategory?.subcategoryId ===
                                  subcategory.id ? (
                                  <>
                                    <button
                                      onPointerDown={handleUpdateSubcategory}
                                      disabled={loading}
                                      className="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 transition-colors disabled:opacity-50 text-sm"
                                    >
                                      Save
                                    </button>
                                    <button
                                      onPointerDown={handleCancelEdit}
                                      className="text-gray-600 dark:text-dark-400 hover:text-gray-900 dark:hover:text-white transition-colors text-sm"
                                    >
                                      Cancel
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onPointerDown={() =>
                                        handleEditSubcategory(
                                          rootCategory.rootKey,
                                          subcategory
                                        )
                                      }
                                      className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 transition-colors"
                                      disabled={
                                        loading || editingSubcategory !== null
                                      }
                                    >
                                      <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button
                                      onPointerDown={() =>
                                        handleDeleteSubcategory(
                                          rootCategory.rootKey,
                                          subcategory.id
                                        )
                                      }
                                      className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 transition-colors"
                                      disabled={
                                        loading || editingSubcategory !== null
                                      }
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </>
                                )}
                              </div>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
                );
              })}
              {filteredCategories.length === 0 && !loading && (
                <div className="text-center py-8">
                  <Search className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-dark-600" />
                  <p className="text-gray-500 dark:text-dark-300 text-lg font-medium">
                    {searchTerm.trim()
                      ? "No categories match your search"
                      : "No root categories found"}
                  </p>
                  <p className="text-gray-400 dark:text-dark-400 text-sm">
                    {searchTerm.trim()
                      ? "Try adjusting your search terms."
                      : retailer === "walmart"
                      ? 'Click "Add Root Category" to create one.'
                      : "HD categories must be created via the API or database."}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
